import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const VIDEO_PACKAGES = [
  {
    name: '4-Hour',
    price: '$1,500',
    note: 'Ceremony Coverage',
    hours: 4,
    features: [
      '4 hours of coverage',
      '1 videographer on-site',
      'Shot on Blackmagic 6K Pro Cinema',
      '3–5 min cinematic highlight reel',
      '1 full-length ceremony film',
      'Your choice of music',
      'Delivered digitally',
    ],
    highlight: false,
  },
  {
    name: '6-Hour',
    price: '$1,900',
    note: 'Most Popular',
    hours: 6,
    features: [
      '6 hours of coverage',
      '1 videographer on-site',
      'Shot on Blackmagic 6K Pro Cinema',
      '3–5 min cinematic highlight reel',
      '1 full-length film',
      'Ceremony + reception coverage',
      'Your choice of music',
      'Delivered digitally',
    ],
    highlight: true,
  },
  {
    name: '8-Hour',
    price: '$2,400',
    note: 'Full Day',
    hours: 8,
    features: [
      '8 hours of coverage',
      '1 videographer on-site',
      'Shot on Blackmagic 6K Pro Cinema',
      '3–5 min cinematic highlight reel',
      '1 full-length film',
      'Getting ready → reception',
      'Your choice of music',
      'Delivered digitally',
    ],
    highlight: false,
  },
];

const PACKAGES = [
  {
    name: 'Starter',
    price: '$299',
    note: 'Limited Time Offer',
    features: [
      '4 hours of coverage',
      '3–5 vertical Reels / TikToks',
      '1 cinematic highlight clip',
      'Delivered within 48 hours',
    ],
    highlight: true,
  },
  {
    name: 'Essential',
    price: '$499',
    note: 'Most Popular',
    features: [
      '6 hours of coverage',
      '6–8 vertical Reels / TikToks',
      '2 cinematic highlight clips',
      'Ceremony & reception coverage',
      'Delivered within 48 hours',
    ],
    highlight: false,
  },
  {
    name: 'Premium',
    price: '$799',
    note: 'Full Day',
    features: [
      'Full day coverage',
      '10+ vertical Reels / TikToks',
      '3 cinematic highlight clips',
      'Getting ready → reception',
      'Priority 24-hour delivery',
    ],
    highlight: false,
  },
];

export default function Weddings() {
  return (
    <>
    <Helmet>
      <title>Cinematic Wedding Videography | Blackmagic Films — NJ, PA & NYC</title>
      <meta name="description" content="Luxury cinematic wedding films shot in 6K on Blackmagic Cinema Cameras. Timeless storytelling for your most important day. Serving New Jersey, Philadelphia PA, and New York City." />
      <link rel="canonical" href="https://www.blackmagicfilms.com/weddings" />
      <meta property="og:title" content="Cinematic Wedding Videography | Blackmagic Films — NJ, PA & NYC" />
      <meta property="og:description" content="Luxury 6K cinematic wedding films. Serving NJ, Philadelphia PA, and NYC. Timeless visual storytelling on Blackmagic Cinema Cameras." />
      <meta property="og:url" content="https://www.blackmagicfilms.com/weddings" />
      <meta property="og:image" content="https://www.blackmagicfilms.com/images/weddings-promo.jpg" />
    </Helmet>
    <div className="min-h-screen bg-black text-white">

      {/* Hero — Content Creation */}
      <div className="relative w-full">
        <img
          src="/images/weddings-promo.png"
          alt="Blackmagic Films Wedding Content Creators"
          className="w-full object-cover"
          loading="eager"
        />
      </div>

      {/* Intro */}
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">
            Philadelphia & New Jersey
          </span>
          <h1 className="text-4xl sm:text-5xl font-black mt-3 mb-5 leading-tight">
            Wedding Content Creators
          </h1>
          <p className="text-neutral-300 text-lg leading-relaxed">
            While your photographer captures the portraits and your videographer films the
            cinematic edit — we capture <span className="text-white font-semibold">your wedding day as it actually felt</span>:
            raw, real, and ready to share. Vertical clips, Reels, and highlights delivered
            within 48 hours so you can post before the weekend is over.
          </p>
        </motion.div>
      </div>

      {/* What You Get */}
      <div className="bg-neutral-950 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-black text-center mb-10">
            What You Get
          </h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {[
              { icon: '🎬', title: '4K Cinema Quality', body: 'Shot on professional cinema cameras — not a phone or a DSLR.' },
              { icon: '📱', title: 'Social-Ready Vertical Content', body: 'Every clip formatted for Instagram Reels, TikTok, and Stories out of the box.' },
              { icon: '⚡', title: '48-Hour Delivery', body: 'Clips in your hands before your honeymoon starts. No waiting months.' },
              { icon: '🎯', title: 'Dedicated On-Site Creator', body: 'One person whose only job is capturing shareable moments — separate from your photographer.' },
            ].map((item) => (
              <div key={item.title} className="bg-black rounded-2xl p-6 ring-1 ring-white/10">
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content Creation Packages */}
      <div className="max-w-5xl mx-auto px-6 py-20">
        <h2 className="text-2xl sm:text-3xl font-black text-center mb-3">Packages</h2>
        <p className="text-center text-neutral-400 mb-12 text-sm">
          Serving Philadelphia, South Jersey, and surrounding areas.
        </p>
        <div className="grid sm:grid-cols-3 gap-6">
          {PACKAGES.map((pkg) => (
            <div
              key={pkg.name}
              className={`relative rounded-2xl p-7 flex flex-col ring-1 ${
                pkg.highlight
                  ? 'bg-amber-400 text-black ring-amber-400'
                  : 'bg-neutral-950 text-white ring-white/10'
              }`}
            >
              {pkg.note && (
                <span className={`text-xs font-bold tracking-widest uppercase mb-4 ${pkg.highlight ? 'text-black/60' : 'text-amber-400'}`}>
                  {pkg.note}
                </span>
              )}
              <h3 className="text-xl font-black mb-1">{pkg.name}</h3>
              <p className={`text-4xl font-black mb-6 ${pkg.highlight ? 'text-black' : 'text-white'}`}>
                {pkg.price}
              </p>
              <ul className="space-y-2 flex-1">
                {pkg.features.map((f) => (
                  <li key={f} className={`text-sm flex gap-2 ${pkg.highlight ? 'text-black/80' : 'text-neutral-300'}`}>
                    <span>✓</span> {f}
                  </li>
                ))}
              </ul>
              <a
                href="mailto:mike@blackmagicfilms.com?subject=Wedding Content Creator Inquiry"
                className={`mt-8 block text-center font-bold text-sm py-3 rounded-full transition ${
                  pkg.highlight
                    ? 'bg-black text-white hover:bg-neutral-900'
                    : 'bg-white text-black hover:opacity-90'
                }`}
              >
                Book This Package
              </a>
            </div>
          ))}
        </div>
        <p className="text-center text-neutral-500 text-xs mt-6">
          * Pricing based on availability. Travel fees may apply outside Philadelphia / South Jersey area.
        </p>
      </div>

      {/* Wedding Videography Hero Image */}
      <div className="relative w-full">
        <img
          src="/images/weddings-videography.png"
          alt="Blackmagic Films Wedding Videography"
          className="w-full object-cover"
          loading="lazy"
        />
      </div>

      {/* Videography Packages */}
      <div className="max-w-5xl mx-auto px-6 py-20">
        <div className="text-center mb-3">
          <span className="text-xs tracking-[0.3em] uppercase text-amber-400 font-medium">Cinema Videography</span>
          <h2 className="text-2xl sm:text-3xl font-black mt-2 mb-3">Wedding Videography Packages</h2>
        </div>
        <p className="text-center text-neutral-400 mb-2 text-sm max-w-xl mx-auto">
          Shot on the Blackmagic 6K Pro — professional cinema cameras used in commercial and documentary production.
        </p>
        <p className="text-center text-neutral-500 mb-12 text-xs max-w-xl mx-auto">
          Every package includes one videographer, a 3–5 minute cinematic highlight reel, and one full-length film with your choice of music, delivered digitally.
        </p>
        <div className="grid sm:grid-cols-3 gap-6">
          {VIDEO_PACKAGES.map((pkg) => (
            <div
              key={pkg.name}
              className={`relative rounded-2xl p-7 flex flex-col ring-1 ${
                pkg.highlight
                  ? 'bg-amber-400 text-black ring-amber-400'
                  : 'bg-neutral-950 text-white ring-white/10'
              }`}
            >
              {pkg.note && (
                <span className={`text-xs font-bold tracking-widest uppercase mb-4 ${pkg.highlight ? 'text-black/60' : 'text-amber-400'}`}>
                  {pkg.note}
                </span>
              )}
              <h3 className="text-xl font-black mb-1">{pkg.name}</h3>
              <p className={`text-4xl font-black mb-6 ${pkg.highlight ? 'text-black' : 'text-white'}`}>
                {pkg.price}
              </p>
              <ul className="space-y-2 flex-1">
                {pkg.features.map((f) => (
                  <li key={f} className={`text-sm flex gap-2 ${pkg.highlight ? 'text-black/80' : 'text-neutral-300'}`}>
                    <span>✓</span> {f}
                  </li>
                ))}
              </ul>
              <a
                href="mailto:mike@blackmagicfilms.com?subject=Wedding Videography Inquiry"
                className={`mt-8 block text-center font-bold text-sm py-3 rounded-full transition ${
                  pkg.highlight
                    ? 'bg-black text-white hover:bg-neutral-900'
                    : 'bg-white text-black hover:opacity-90'
                }`}
              >
                Book This Package
              </a>
            </div>
          ))}
        </div>
        <p className="text-center text-neutral-500 text-xs mt-6">
          * Travel fees may apply outside the Philadelphia / South Jersey area. Editing turnaround is typically 4–6 weeks.
        </p>
      </div>

      {/* CTA */}
      <div className="bg-neutral-950 py-20 px-6 text-center">
        <h2 className="text-3xl sm:text-4xl font-black mb-4">Ready to Book?</h2>
        <p className="text-neutral-400 mb-8 max-w-md mx-auto">
          Dates fill fast. Reach out now to check availability for your wedding.
        </p>
        <a
          href="mailto:mike@blackmagicfilms.com?subject=Wedding Inquiry"
          className="inline-block bg-amber-400 text-black font-bold px-8 py-4 rounded-full text-lg hover:bg-amber-300 transition"
        >
          Email Us to Book
        </a>
        <p className="text-neutral-500 text-sm mt-5">mike@blackmagicfilms.com</p>
      </div>

      {/* Footer */}
      <div className="py-8 text-center text-neutral-600 text-sm">
        © {new Date().getFullYear()} Blackmagic Films — Philadelphia & New Jersey
      </div>

    </div>
    </>
  );
}
